import { get } from "svelte/store";
import { user } from "$lib/stores/auth";
import { PhilatelicItem, type ReviewState, type ObjectId, type UserDashboardEntry, type UserPatch, type Role, type WithoutIdAndReview } from "@briefmarken_katalog/types";
import { parseJSONtoPhilatelicItems, parseJSONtoPhilatelicItem } from "$lib/utils/utils"

const apiUrl = import.meta.env.VITE_API_URL;

class APIRequest
{
    private url: string;
    private payload: any;
    private login:()=>Promise<string>;

    constructor(url: string, verb: string, login:()=>Promise<string>)
    {
        this.url = url;
        this.payload = {
            method: verb,
            headers: {},
        };
        this.login = login;
    }
    
    protected setAuthToken(token: string)
    {
        this.setHeader('x-auth-token', token);
    }

    protected setContentType(value: string)
    {
        this.setHeader('Content-Type', value);
    }

    private setHeader(key: string, value: string)
    {
        this.payload.headers[key] = value;
    }

    protected setBody(content: string)
    {
        this.payload.body = content;
    }

    public async executeInternal(): Promise<Response>
    {
        let res: Response | null = null;
        try {
            // The catalog can be mounted before login (mobile devices are locked to
            // catalog mode), so there may be no user yet. Ask for a login instead of
            // sending a request that is bound to fail with 401.
            let token = get(user)?.token;
            if (!token)
            {
                await this.login();
                token = get(user)?.token;
            }
            if (token) this.setAuthToken(token);
            res = await fetch(this.url, this.payload);
        }
        catch (e:any)
        {
            console.error(e.message);
            throw (e);
        }

        if (!res.ok)
        {
            const error = await res!.json();
            if (res.status == 401)
            {
                await this.login();
                return await this.executeInternal();
            }
            throw new Error(error.message);
        }
        return res;
    }
}

export class PostPhilatelicItemSuggestion extends APIRequest
{
  constructor(item: WithoutIdAndReview<PhilatelicItem>, login:()=>Promise<string>)
  {
    super(`${apiUrl}/stamp_catalog/suggestions`, "POST", login);
    this.setContentType("application/json");
    this.setBody(JSON.stringify(item));
  }
  public async execute(): Promise<void> 
  {
    await this.executeInternal();
  }
};

export class PostPhilatelicItem extends APIRequest
{
  constructor(item: PhilatelicItem, login:()=>Promise<string>)
  {
    super(`${apiUrl}/stamp_catalog/items`, "POST", login);
    this.setContentType("application/json");
    this.setBody(JSON.stringify(item));
  }
  public async execute(): Promise<void> 
  {
    await this.executeInternal();
  }
};

export class PostPhilatelicItemsBatch extends APIRequest
{
  constructor(items: PhilatelicItem[], login: () => Promise<string>)
  {
    super(`${apiUrl}/stamp_catalog/items/batch`, 'POST', login);
    this.setContentType('application/json');
    this.setBody(JSON.stringify(items));
  }
  public async execute(): Promise<void>
  {
    await this.executeInternal();
  }
}

export class DeleteAllPhilatelicItems extends APIRequest
{
  constructor(login:()=>Promise<string>)
  {
    super(`${apiUrl}/stamp_catalog`, "DELETE", login);
  }
  public async execute(): Promise<void>
  {
    await this.executeInternal();
  }
}

export class DeleteSuggestion extends APIRequest
{
  constructor(id: ObjectId, login: () => Promise<string>)
  {
    super(`${apiUrl}/stamp_catalog/suggestions/${id}`, 'DELETE', login);
  }
  public async execute(): Promise<void>
  {
    await this.executeInternal();
  }
}

export class MoveItemToReview extends APIRequest
{
  constructor(id: ObjectId, login: () => Promise<string>)
  {
    super(`${apiUrl}/stamp_catalog/items/${id}/review`, 'POST', login);
  }
  public async execute(): Promise<void>
  {
    await this.executeInternal();
  }
}

export class ReviewSuggestion extends APIRequest
{
  constructor(id: ObjectId, state: ReviewState, updates: Record<string, any> | undefined, login: () => Promise<string>)
  {
    super(`${apiUrl}/stamp_catalog/reviews/${id}`, "POST", login);
    this.setContentType("application/json");
    this.setBody(JSON.stringify({ state, updates }));
  }
  /** Returns the stored document as the server wrote it, so callers can refresh their view immediately. */
  public async execute(): Promise<PhilatelicItem | null>
  {
    const res = await this.executeInternal();
    const body = await res.text();
    if (!body) return null;
    return parseJSONtoPhilatelicItem(body);
  }
}

export class GetUploadToken extends APIRequest
{
  constructor(login: () => Promise<string>)
  {
    super(`${apiUrl}/upload-token`, 'POST', login);
  }
  public async execute(): Promise<{ token: string }>
  {
    const res = await this.executeInternal();
    return res.json();
  }
}

export enum GetOneType { Item, Suggestion };
export class GetOne extends APIRequest
{
  constructor(type: GetOneType, id: string, login: () => Promise<string>)
  {
    super(`${apiUrl}/stamp_catalog/${type === GetOneType.Item ? 'item' : 'suggestion'}/${id}`, 'GET', login);
    this.setContentType('application/json');
  }
  public async execute(): Promise<PhilatelicItem | null>
  {
    try {
      const res: Response = await this.executeInternal();
      return parseJSONtoPhilatelicItem(await res.text());
    }
    catch (e: any) {
      alert(`API Error: ${e.message || 'Unknown Error'}`);
      return null;
    }
  }
}

export class PatchUser extends APIRequest
{
  constructor(id: string, patch: UserPatch, login: () => Promise<string>)
  {
    super(`${apiUrl}/admin/users/${id}`, 'PATCH', login);
    this.setContentType('application/json');
    this.setBody(JSON.stringify(patch));
  }
  public async execute(): Promise<void>
  {
    await this.executeInternal();
  }
}

export class GetUserDashboard extends APIRequest
{
  constructor(login: () => Promise<string>)
  {
    super(`${apiUrl}/admin/users`, 'GET', login);
  }
  public async execute(): Promise<UserDashboardEntry[]>
  {
    const res = await this.executeInternal();
    return res.json();
  }
}

export class DeleteUser extends APIRequest
{
  constructor(id: string, login: () => Promise<string>)
  {
    super(`${apiUrl}/admin/users/${id}`, 'DELETE', login);
  }
  public async execute(): Promise<void>
  {
    await this.executeInternal();
  }
}

export class RegisterUser extends APIRequest
{
  constructor(data: { email: string; password: string; role: Role; name: string }, login: () => Promise<string>)
  {
    super(`${apiUrl}/auth/register`, 'POST', login);
    this.setContentType('application/json');
    this.setBody(JSON.stringify(data));
  }
  public async execute(): Promise<void>
  {
    await this.executeInternal();
  }
}

export enum GetAllType { Items, Suggestions };
export interface PagedResult {
  items: PhilatelicItem[];
  total: number;
  page: number;
  limit: number;
}
export class GetPage extends APIRequest
{
  private getAllType: GetAllType;
  constructor(type: GetAllType, page: number, limit: number, login: () => Promise<string>)
  {
    const path = type === GetAllType.Items ? 'items' : 'suggestions';
    super(`${apiUrl}/stamp_catalog/${path}?page=${page}&limit=${limit}`, 'GET', login);
    this.getAllType = type;
  }
  public async execute(): Promise<PagedResult | null>
  {
    let res: Response;
    try {
      res = await this.executeInternal();
    } catch (e: any) {
      alert(`API Error: Failed to fetch ${this.getAllType === GetAllType.Items ? 'items' : 'suggestions'} page. ${e.message || 'Unknown Error'}`);
      return null;
    }
    try {
      const data = JSON.parse(await res.text(), (key, value) => {
        if (key === 'date_of_issue' || key === 'expiry_date') return new Date(value);
        return value;
      });
      return data as PagedResult;
    } catch (e: any) {
      alert(`API Error: Response was not valid JSON. ${e.message || 'Unknown Error'}`);
      return null;
    }
  }
}